import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Loads the next page of results into the grid, on demand.
   *
   * Story 19.12 / AC-C1+C2 — o catálogo trocou a paginação numerada por um controle manual
   * e visível. O append é o do `PaginatedList` (`loadNextPage`), por isso o filtro e o
   * `sort_by` atuais viajam junto: a próxima página é buscada a partir da URL corrente, e
   * uma troca de filtro limpa o cache de páginas antes de qualquer novo clique.
   *
   * O botão fica desativado durante a busca (um clique nervoso não acrescenta a mesma
   * página duas vezes) e é retirado quando não há página seguinte.
   *
   * @returns {Promise<void>}
   */
  async loadMore() {
    const { loadMoreButton } = this.refs;

    if (!(loadMoreButton instanceof HTMLButtonElement) || loadMoreButton.disabled) return;

    loadMoreButton.disabled = true;
    loadMoreButton.setAttribute('aria-busy', 'true');

    try {
      await this.loadNextPage();
      /* Os `refs` do componente (incluindo `cards`, de onde sai "qual é a próxima página")
         são recalculados por um MutationObserver, logo só depois dos microtasks desta
         tarefa. Esperar um frame antes de perguntar `hasNextPage` evita decidir com a lista
         de cards ANTERIOR ao append — que deixaria o botão aceso na última página. */
      await new Promise((resolve) => requestAnimationFrame(() => resolve()));
    } finally {
      loadMoreButton.removeAttribute('aria-busy');

      if (this.hasNextPage) {
        loadMoreButton.disabled = false;
        /* `preventScroll` — devolver o foco ao botão sem o navegador atirar a página para
           ele. O mesmo `focus()` desprotegido já fez a PDP voar sozinha no primeiro load. */
        loadMoreButton.focus({ preventScroll: true });
      } else {
        (loadMoreButton.closest('.tb-load-more') ?? loadMoreButton).remove();
      }
    }
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
